<script setup>
import DayPart from "./DayPart.vue";
import {computed, ref, watch} from "vue";
import { trans } from 'laravel-vue-i18n';
import Button from "primevue/button";
import {FloatLabel} from "primevue";
import DatePicker from "primevue/datepicker";
import Popover from "primevue/popover";
import ConfirmDialog from "primevue/confirmdialog";

defineProps({
    slotHeight: Number,
    formType: String
})

const emit = defineEmits(['changeView'])

const selectedWorker = defineModel('selectedWorker');

const days = ref([
    { day: trans('common.dow_short.mon'), identifier: 'mon', shortcut: 'mon', slots: [] },
    { day: trans('common.dow_short.tue'), identifier: 'tue', shortcut: 'tue', slots: [] },
    { day: trans('common.dow_short.wed'), identifier: 'wed', shortcut: 'wed', slots: [] },
    { day: trans('common.dow_short.thu'), identifier: 'thu', shortcut: 'thu', slots: [] },
    { day: trans('common.dow_short.fri'), identifier: 'fri', shortcut: 'fri', slots: [] },
    { day: trans('common.dow_short.sat'), identifier: 'sat', shortcut: 'sat', slots: [] },
    { day: trans('common.dow_short.sun'), identifier: 'sun', shortcut: 'sun', slots: []
    },
]);

const timeFrom = ref(new Date('2020-01-01T06:00:00.000'));
const timeTo = ref(new Date('2020-01-01T18:00:00.000'));

const computedDays = computed(() => {
    if (selectedWorker.value)
        return days.value.filter(day => day.slots.filter(slot => slot.worker === selectedWorker.value).length)

    return days.value;
})

watch([timeFrom, timeTo], () => emit('changeView'))

watch(days, () => {
  let allStarts = [];
  let allEnds = [];
  days.value.forEach(day => {
    day.slots.forEach(slot => {
      const [sh, sm] = slot.start.split(':').map(Number);
      const [eh, em] = slot.end.split(':').map(Number);
      allStarts.push(sh + sm / 60);
      allEnds.push(eh + em / 60);
    });
  });

  const defaultFrom = 6;
  const defaultTo = 18;

  if (allStarts.length) {
    const earliest = Math.min(...allStarts);
    const currentFrom = timeFrom.value.getHours() + timeFrom.value.getMinutes() / 60;
    if (earliest < currentFrom) {
      timeFrom.value = new Date(`2020-01-01T${String(Math.floor(earliest)).padStart(2,'0')}:${String(Math.round((earliest%1)*60)).padStart(2,'0')}:00.000`);
    }
  }

  if (allEnds.length) {
    const latest = Math.max(...allEnds);
    const currentTo = timeTo.value.getHours() + timeTo.value.getMinutes() / 60;
    if (latest > currentTo) {
      timeTo.value = new Date(`2020-01-01T${String(Math.floor(latest)).padStart(2,'0')}:${String(Math.round((latest%1)*60)).padStart(2,'0')}:00.000`);
    }
  }
}, { immediate: true, deep: true });

const increment = ref(0.5);
const activeEvent = ref(null);
const model = defineModel();
const form = defineModel('form');


const times = computed(() => {
    let slots = [];
    for (let time = timeFrom.value.getHours(); time <= timeTo.value.getHours(); time += increment.value) {
        slots.push(time);
    }
    return slots;
})

const numberToTime = (num) => {
    const hours = Math.floor(num)
    const minutes = (num - hours) * 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

const getLegacyObj = (identifier) => {
    return {
        timeslots: days.value.find(d => d.shortcut === identifier).slots.map(d => {
            return {
                time_start: d.start,
                time_end: d.end,
                interval: d.interval,
                pause: d.pause,
                blocking: d.type !== 'work',
                exclude_workers: [],
                service_length_override: d.service_length_override,
                id_reservation_workers: d.worker
            }
        }),
        id: model.value.days[identifier]?.id ?? identifier,
        label: days.value.find(d => d.shortcut === identifier).day,
        selected: days.value.find(d => d.shortcut === identifier).slots.length > 0
    }
}

const loadFromLegacyObj = (legacyObj, shortcut) => {
    return {
        day: legacyObj.label,
        identifier: legacyObj.id,
        shortcut: shortcut,
        slots: legacyObj.timeslots.filter(el => el.time_start && el.time_end).map(d => {
            return {
                start: d.time_start,
                end: d.time_end,
                interval: d.interval,
                pause: d.pause,
                service_length_override: d.service_length_override,
                type: d.blocking ? 'standard' : 'work',
                worker: d.id_reservation_workers ?? null
            }
        })
    }
}

let skipModelWatcher = false;
let skipDaysWatcher = false;

watch(() => model.value, (now) => {
    if (skipModelWatcher) {
      skipModelWatcher = false;
      return;
    }
    if (now && now?.days) {
      skipDaysWatcher = true;
      days.value = [
        loadFromLegacyObj(now.days.mon, 'mon'),
        loadFromLegacyObj(now.days.tue, 'tue'),
        loadFromLegacyObj(now.days.wed, 'wed'),
        loadFromLegacyObj(now.days.thu, 'thu'),
        loadFromLegacyObj(now.days.fri, 'fri'),
        loadFromLegacyObj(now.days.sat, 'sat'),
        loadFromLegacyObj(now.days.sun, 'sun'),
      ];
    }
}, { immediate: true, deep: true });

watch(() => days.value, () => {
    if (skipDaysWatcher) {
      skipDaysWatcher = false;
      return;
    }
    skipModelWatcher = true;

    if (model.value?.days) {
        model.value.days = {
            mon: getLegacyObj('mon'),
            tue: getLegacyObj('tue'),
            wed: getLegacyObj('wed'),
            thu: getLegacyObj('thu'),
            fri: getLegacyObj('fri'),
            sat: getLegacyObj('sat'),
            sun: getLegacyObj('sun'),
        };
    }
}, { deep: true, immediate: true });


const popDown = ref();
const popUp = ref();

const toggleDown = (event) => {
    popDown.value.toggle(event);
};

const toggleUp = (event) => {
    popUp.value.toggle(event);
};

// Computed for earliestSlotDate and latestSlotDate
const earliestSlotDate = computed(() => {
  let allStarts = [];
  days.value.forEach(day => {
    day.slots.forEach(slot => {
      const [sh, sm] = slot.start.split(':').map(Number);
      allStarts.push(new Date(`2020-01-01T${String(sh).padStart(2,'0')}:${String(sm).padStart(2,'0')}:00.000`));
    });
  });
  if (allStarts.length) {
    return new Date(Math.min(...allStarts.map(d => d.getTime())));
  }
  return null;
});

const latestSlotDate = computed(() => {
  let allEnds = [];
  days.value.forEach(day => {
    day.slots.forEach(slot => {
      const [eh, em] = slot.end.split(':').map(Number);
      allEnds.push(new Date(`2020-01-01T${String(eh).padStart(2,'0')}:${String(em).padStart(2,'0')}:00.000`));
    });
  });
  if (allEnds.length) {
    return new Date(Math.max(...allEnds.map(d => d.getTime())));
  }
  return null;
});

</script>

<template>
    <div class="flex   pl-4 dark:bg-neutral-900 dark:border-neutral-700 bg-neutral-100 shadow-sm relative" :key="slotHeight">
        <div class="flex flex-col text-center relative border-r dark:border-neutral-700 pr-4">
            <div class="h-10"></div>
            <div class="absolute flex justify-center items-center ml-1 mt-1 top-0">
                <Button icon="pi pi-chevron-up" @click="toggleUp" size="small" text severity="secondary" rounded></Button>
                <Popover ref="popUp">
                    <FloatLabel class="!w-40" variant="on">
                        <DatePicker
                            v-model="timeFrom"
                            showIcon
                            :max-date="earliestSlotDate"
                            fluid
                            iconDisplay="input"
                            timeOnly
                        >
                            <template #inputicon="slotProps">
                                <i class="pi pi-clock" @click="slotProps.clickCallback" />
                            </template>
                        </DatePicker>
                        <label for="over_label">{{ $t('forms.timeslot_display_from') }}</label>
                    </FloatLabel>
                </Popover>
            </div>
            <div v-for="time in times.filter(el => el % 1 === 0)" :style="{ height: `${slotHeight}px` }" class="text-sm opacity-60">{{numberToTime(time)}}</div>

            <div class="absolute flex justify-center items-center ml-1 mb-1 bottom-0">
                <Button icon="pi pi-chevron-down" id="show-more" @click="toggleDown" size="small" text severity="secondary" rounded></Button>
                <Popover ref="popDown">
                    <FloatLabel class="!w-40" variant="on">
                        <DatePicker
                            v-model="timeTo"
                            showIcon
                            :min-date="latestSlotDate"
                            fluid
                            iconDisplay="input"
                            timeOnly
                        >
                            <template #inputicon="slotProps">
                                <i class="pi pi-clock" @click="slotProps.clickCallback" />
                            </template>
                        </DatePicker>
                        <label for="over_label">{{ $t('forms.timeslot_display_to') }}</label>
                    </FloatLabel>
                </Popover>
            </div>
        </div>
        <day-part v-for="(day, index) in days" :id="`day-${index}`" :index="index" :day="day.day" :form-type="formType" v-model:active-event="activeEvent" v-model:form="form" :selected-worker="selectedWorker" v-model="day.slots" :slot-height="slotHeight" :min-time="timeFrom.getHours()" :max-time="timeTo.getHours()" :increment="increment"></day-part>

        <ConfirmDialog group="mass-setter"></ConfirmDialog>
        <div v-if="!computedDays.length" class="absolute w-full h-full flex justify-center  flex-col pointer-events-none items-center">
            <div class="flex flex-col justify-center items-center bg-white p-4 px-8 shadow-xl rounded-xl dark:border-neutral-700 dark:bg-black border">
                <template v-if="selectedWorker">
                    <h2 class="text-lg font-bold">{{ $t('forms.timeslot_custom_plan') }}</h2>
                    <p>{{ $t('forms.timeslot_custom_plan_desc') }}</p>
                </template>
                <template v-else>
                    <h2 class="text-lg font-bold">{{ $t('forms.timeslot_empty_plan') }}</h2>
                    <p>{{ $t('forms.timeslot_empty_plan_desc') }}</p>
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
